import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { Copy, Crown, LogOut, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { ICON_PICKER_OPTIONS, RoomIcon } from "@/components/home/room-icon";
import { AddRoomTile } from "@/components/home/add-room-tile";
import { Splash } from "@/components/shell/splash";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { BackLink } from "@/components/shell/back-link";
import { RoomPlanLine } from "@/components/board/room-plan-dialog";
import { Wordmark } from "@/components/shell/wordmark";
import { BookmarkletLink } from "@/components/shell/bookmarklet-link";
import { DeleteAccount } from "@/components/account/delete-account";
import { EmailConfirmation } from "@/components/account/email-confirmation";
import { PlanSection } from "@/components/billing/plan-section";
import { LanguagePicker } from "@/components/shell/language-picker";
import { SupportForm } from "@/components/shell/support-form";
import { ClientsSection } from "@/components/workspaces/clients-section";
import { WorkspaceList } from "@/components/workspaces/workspace-list";
import { boardName } from "@/i18n/catalog";
import { initialsOf } from "@/lib/format";
import { cn } from "@/lib/utils";
import { said } from "@/lib/errors";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <SignedInSettings />
      </Authenticated>
      <Toaster />
    </>
  );
}

function SignedInSettings() {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation();
  const me = useQuery(api.auth.getCurrentUser);
  const workspace = useQuery(api.workspaces.getMyWorkspace);
  const members = useQuery(api.workspaces.getMembers);
  const data = useQuery(api.boards.listForMyWorkspace);
  const plan = useQuery(api.billing.getMyPlan);

  const renameWorkspace = useMutation(api.workspaces.rename);
  const createInvite = useMutation(api.workspaces.createInvite);
  const deleteBoard = useMutation(api.boards.deleteBoard);

  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Doc<"boards"> | null>(
    null,
  );
  const [editingRoom, setEditingRoom] = useState<Doc<"boards"> | null>(null);

  useEffect(() => {
    if (workspace && !renameValue) setRenameValue(workspace.name);
  }, [workspace, renameValue]);

  // Stripe sends the owner back here after a purchase.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Thank you. The plan updates here as soon as Stripe confirms the payment.");
      window.history.replaceState(null, "", "/settings");
    }
  }, []);

  // The top bar's help link arrives while this page is still a splash, with
  // nothing yet to scroll to.
  const ready = workspace != null && data != null && me !== undefined;
  useEffect(() => {
    if (ready && window.location.hash === "#help") {
      document.getElementById("help")?.scrollIntoView();
    }
  }, [ready]);

  if (workspace === undefined || data === undefined || me === undefined)
    return <Splash subtitle={tc("status.loading")} />;
  if (workspace === null || data === null)
    return <RedirectTo to="/onboarding" />;

  const userId = (me?._id ?? "") as string;
  const isOwner = workspace.ownerId === userId;
  // A client of the team sees the workspace and their own account, not its plan, rooms or people to manage.
  const isTeam = workspace.role === "team";
  const studio = plan?.plan === "studio";
  const team = (members ?? []).filter((m) => m.role === "team");
  const existingRoomTypes = new Set(
    data.boards
      .filter((b) => b.kind === "room" && b.roomType && !b.recipeId)
      .map((b) => b.roomType!),
  );
  const existingRecipeIds = new Set(
    data.boards
      .filter((b) => b.kind === "room" && b.recipeId)
      .map((b) => b.recipeId!),
  );
  const rooms = data.boards.filter((b) => b.kind === "room");
  const freeform = data.boards.filter((b) => b.kind === "freeform");

  async function onRename(event: React.FormEvent) {
    event.preventDefault();
    setSavingRename(true);
    try {
      await renameWorkspace({ name: renameValue });
      toast.success(t("workspace.renamed"));
    } catch (error) {
      toast.error(said(error, t("workspace.renameFailed")));
    } finally {
      setSavingRename(false);
    }
  }

  async function onCreateInvite() {
    try {
      const code = await createInvite();
      setInviteCode(code);
    } catch (error) {
      toast.error(said(error, t("workspace.invite.failed")));
    }
  }

  async function onConfirmDelete() {
    if (!confirmDelete) return;
    try {
      await deleteBoard({ boardId: confirmDelete._id as Id<"boards"> });
      toast.success(t("deleteDialog.deleted", {
          name: boardName(confirmDelete.name),
        }));
      setConfirmDelete(null);
    } catch (error) {
      toast.error(said(error, t("deleteDialog.failed")));
    }
  }

  return (
    // A phone column on phones; from a tablet up, each section's heading takes a column of its own beside it
    <main className="mx-auto min-h-svh w-full max-w-3xl px-6 pb-24 md:max-w-6xl md:px-10">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 -mx-6 border-b border-line bg-paper/85 px-6 backdrop-blur-md md:-mx-10 md:px-10">
        <div className="flex h-12 items-center justify-between">
          <BackLink to="/" label={t("page.backToBoards")} />
          <Wordmark size="sm" />
        </div>
      </div>

      <header className="grid gap-2 py-10 sm:py-14">
        <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">
          {t("page.eyebrow")}
        </span>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight text-ink">
          <span className="italic">{t("page.title")}</span>
        </h1>
      </header>

      <div className="grid gap-16 md:gap-20">
        {/* Account */}
        <Section
          eyebrow={t("account.eyebrow")}
          title={
            <Trans
              t={t}
              i18nKey="account.title"
              values={{ email: me?.email ?? "—" }}
              components={{ em: <span className="italic" /> }}
            />
          }
        >
          <dl className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("account.name")}
              value={(me as { name?: string } | null)?.name ?? "—"}
            />
            <Field label={t("account.email")} value={me?.email ?? "—"} />
          </dl>
          {me ? (
            <EmailConfirmation email={me.email} verified={me.emailVerified} />
          ) : null}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await authClient.signOut();
                window.location.assign("/login");
              }}
            >
              <LogOut className="size-3.5" />
              {t("account.signOut")}
            </Button>
            {me ? <DeleteAccount email={me.email} /> : null}
          </div>
        </Section>

        {/* Plan & credits */}
        {isTeam ? (
        <Section
          eyebrow="Plan"
          title={
            studio ? (
              <>What the <span className="italic">agency</span> has.</>
            ) : (
              <>What the <span className="italic">household</span> has.</>
            )
          }
          description={
            studio
              ? "One plan for every workspace you own: the visits are shared between them, and your clients use them only when you let them change a room."
              : "Boards, items, comments and palettes are free for everyone. 3D visits use credits; the owner pays, everyone in the workspace benefits."
          }
        >
          <PlanSection ownerName={members?.find((m) => m.isOwner)?.name ?? null} />
        </Section>
        ) : null}

        {/* Workspaces: the ones the user is in, a new one on Studio, a code to join another */}
        <Section
          eyebrow={t("workspaces.eyebrow")}
          title={
            <Trans
              t={t}
              i18nKey="workspaces.title"
              components={{ em: <span className="italic" /> }}
            />
          }
          description={t(studio ? "workspaces.descriptionStudio" : "workspaces.description")}
        >
          <WorkspaceList />
        </Section>

        {/* Clients, on Studio: who they are and what each may do */}
        {isTeam && studio ? (
          <Section
            eyebrow={t("clients.eyebrow")}
            title={
              <Trans
                t={t}
                i18nKey="clients.title"
                components={{ em: <span className="italic" /> }}
              />
            }
            description={t("clients.description")}
          >
            <ClientsSection />
          </Section>
        ) : null}

        {/* Language */}
        <Section
          eyebrow={t("language.eyebrow")}
          title={
            <Trans
              t={t}
              i18nKey="language.title"
              components={{ em: <span className="italic" /> }}
            />
          }
          description={t("language.hint")}
        >
          <LanguagePicker className="max-w-md" />
        </Section>

        {/* Bookmarklet */}
        <Section
          eyebrow={t("browser.eyebrow")}
          title={
            <Trans
              t={t}
              i18nKey="browser.title"
              components={{ em: <span className="italic" /> }}
            />
          }
          description={t("browser.description")}
        >
          <BookmarkletLink />
        </Section>

        {/* Help */}
        <Section
          id="help"
          eyebrow={t("help.eyebrow")}
          title={
            <Trans
              t={t}
              i18nKey="help.title"
              components={{ em: <span className="italic" /> }}
            />
          }
          description={t("help.description")}
        >
          <SupportForm email={me?.email} />
        </Section>

        {/* Workspace */}
        <Section
          eyebrow={t("workspace.eyebrow")}
          title={
            <>
              {isOwner ? t("workspace.owner") : isTeam ? t("workspace.member") : t("workspace.client")}{" "}
              <span className="text-ink-3">
                {t("workspace.memberCount", { count: workspace.memberCount })}
              </span>
            </>
          }
        >
          {isOwner ? (
            <form onSubmit={onRename} className="grid gap-3 max-w-md">
              <Label htmlFor="ws-name">{t("workspace.name")}</Label>
              <div className="flex items-end gap-3">
                <Input
                  id="ws-name"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className="text-lg font-display"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    savingRename ||
                    !renameValue.trim() ||
                    renameValue.trim() === workspace.name
                  }
                >
                  {tc("actions.save")}
                </Button>
              </div>
            </form>
          ) : (
            <Field label={t("workspace.workspaceName")} value={workspace.name} />
          )}

          <div className="mt-8 grid gap-3">
            <Label>{t("workspace.members")}</Label>
            <ul className="grid grid-cols-1 divide-y divide-line border-y border-line">
              {team.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={cn(
                        "inline-flex size-7 items-center justify-center rounded-full bg-paper-3 text-[10px] font-medium text-ink-2 num",
                        m.isMe && "bg-ink text-paper",
                      )}
                    >
                      {initialsOf(m.name, m.email)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-ink">
                        {m.name ?? m.email ?? t("workspace.someone")}
                        {m.isMe ? (
                          <span className="ms-2 text-2xs uppercase tracking-wider text-ink-3">
                            {t("workspace.you")}
                          </span>
                        ) : null}
                      </p>
                      {m.email && m.name ? (
                        <p className="truncate text-xs text-ink-3">{m.email}</p>
                      ) : null}
                    </div>
                  </div>
                  {m.isOwner ? (
                    <span className="inline-flex items-center gap-1 text-2xs uppercase tracking-wider text-ink-3">
                      <Crown className="size-3" />
                      {t("workspace.ownerBadge")}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>

          {isOwner ? (
            <div className="mt-8 grid gap-3">
              <Label>{t("workspace.invite.label")}</Label>
              {inviteCode ? (
                <div className="flex items-center justify-between gap-3 border-y border-line py-4">
                  <span className="font-display text-2xl tracking-[0.3em] text-ink num">
                    {inviteCode}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      await navigator.clipboard.writeText(inviteCode);
                      toast.success(t("workspace.invite.copied"));
                    }}
                  >
                    <Copy className="size-3.5" />
                    {tc("actions.copy")}
                  </Button>
                </div>
              ) : null}
              <div>
                <Button variant="outline" size="sm" onClick={onCreateInvite}>
                  {inviteCode
                    ? t("workspace.invite.regenerate")
                    : t("workspace.invite.generate")}
                </Button>
              </div>
            </div>
          ) : null}
        </Section>

        {/* Rooms */}
        {isTeam ? (
        <Section
          eyebrow={t("rooms.eyebrow")}
          title={
            <Trans
              t={t}
              i18nKey="rooms.title"
              components={{ em: <span className="italic" /> }}
            />
          }
          description={t("rooms.description")}
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-3">
            {rooms.map((board) => (
              <BoardChip
                key={board._id}
                board={board}
                onEdit={() => setEditingRoom(board)}
                onDelete={() => setConfirmDelete(board)}
              />
            ))}
            <AddRoomTile
              existingRoomTypes={existingRoomTypes}
              existingRecipeIds={existingRecipeIds}
              variant="chip"
            />
            {rooms.length === 0 ? (
              <p className="col-span-full text-xs text-ink-3">
                {t("rooms.empty")}
              </p>
            ) : null}
          </div>
        </Section>
        ) : null}

        {/* Freeform */}
        {freeform.length > 0 && isTeam ? (
          <Section
            eyebrow={t("themes.eyebrow")}
            title={
              <Trans
                t={t}
                i18nKey="themes.title"
                components={{ em: <span className="italic" /> }}
              />
            }
            description={t("themes.description")}
          >
            <ul className="grid grid-cols-1 divide-y divide-line border-y border-line">
              {freeform.map((board) => (
                <li
                  key={board._id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <span className="font-display text-lg text-ink">
                    {boardName(board.name)}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDelete(board)}
                    aria-label={t("themes.deleteAria", {
                      name: boardName(board.name),
                    })}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}
      </div>

      <EditRoomDialog
        room={editingRoom}
        onClose={() => setEditingRoom(null)}
      />

      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans
                t={t}
                i18nKey="deleteDialog.title"
                values={{
                  name: confirmDelete ? boardName(confirmDelete.name) : "",
                }}
                components={{ em: <span className="italic" /> }}
              />
            </DialogTitle>
            <DialogDescription>{t("deleteDialog.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDelete(null)}
            >
              {tc("actions.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={onConfirmDelete}
            >
              <Trash2 className="size-3.5" />
              {tc("actions.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function Section({
  id,
  eyebrow,
  title,
  description,
  children,
}: {
  id?: string;
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className="grid scroll-mt-20 gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-x-12 md:border-t md:border-line-strong md:pt-6 lg:gap-x-20"
    >
      {/* beside the section from a tablet up, staying in view while its controls scroll past */}
      <header className="grid content-start gap-2 border-b border-line-strong pb-3 md:sticky md:top-20 md:self-start md:border-b-0 md:pb-0">
        <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">
          {eyebrow}
        </span>
        <h2 className="font-display text-2xl leading-tight text-ink text-balance [overflow-wrap:anywhere]">
          {title}
        </h2>
        {description ? (
          <p className="text-xs text-ink-3 max-w-prose">{description}</p>
        ) : null}
      </header>
      <div>{children}</div>
    </section>
  );
}

function BoardChip({
  board,
  onEdit,
  onDelete,
}: {
  board: Doc<"boards">;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("settings");
  return (
    <button
      type="button"
      onClick={onEdit}
      className="group flex w-full items-center justify-between gap-2 border border-line bg-paper px-3 py-3 rounded-md text-start text-sm hover:border-ink-3 hover:bg-paper-2/60 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
      aria-label={t("rooms.editAria", { name: boardName(board.name) })}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2 text-ink">
        <RoomIcon
          roomType={board.roomType}
          icon={board.icon}
          className="size-3.5 text-ink-3 shrink-0"
        />
        <span className="line-clamp-2">{boardName(board.name)}</span>
      </span>
      <span className="flex shrink-0 items-center gap-0.5">
        <span
          aria-hidden
          className="inline-flex size-6 items-center justify-center rounded-md text-ink-4 opacity-0 transition-all group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          <Pencil className="size-3" />
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              onDelete();
            }
          }}
          aria-label={t("rooms.removeAria", { name: boardName(board.name) })}
          className="inline-flex size-6 items-center justify-center rounded-md text-ink-4 opacity-0 transition-all group-hover:opacity-100 group-focus-visible:opacity-100 hover:text-destructive hover:bg-destructive/5 cursor-pointer"
        >
          <Trash2 className="size-3" />
        </span>
      </span>
    </button>
  );
}

function EditRoomDialog({
  room,
  onClose,
}: {
  room: Doc<"boards"> | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={room !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {room ? (
        <EditRoomDialogContent
          key={room._id as string}
          room={room}
          onClose={onClose}
        />
      ) : (
        <DialogContent />
      )}
    </Dialog>
  );
}

function EditRoomDialogContent({
  room,
  onClose,
}: {
  room: Doc<"boards">;
  onClose: () => void;
}) {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation();
  const updateRoom = useMutation(api.boards.updateRoom);
  // Default room names are stored in English and shown translated; only send
  // a name when the user actually changed it, so it keeps following the
  // viewer's language otherwise.
  const shownName = boardName(room.name);
  const [name, setName] = useState(shownName);
  const [description, setDescription] = useState(room.description ?? "");
  const [icon, setIcon] = useState<string | null>(room.icon ?? null);
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const dirty =
    trimmed !== shownName ||
    description.trim() !== (room.description ?? "") ||
    (icon ?? null) !== (room.icon ?? null);
  const selectedIconOption = ICON_PICKER_OPTIONS.find((o) => o.key === icon);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!trimmed) return;
    setSaving(true);
    try {
      await updateRoom({
        boardId: room._id as Id<"boards">,
        name: trimmed !== shownName ? trimmed : undefined,
        description,
        icon: icon ?? "",
      });
      toast.success(t("editRoom.saved", { name: trimmed }));
      onClose();
    } catch (error) {
      toast.error(said(error, t("editRoom.failed")));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>
          <Trans
            t={t}
            i18nKey="editRoom.title"
            values={{ name: shownName }}
            components={{ em: <span className="italic" /> }}
          />
        </DialogTitle>
        <DialogDescription>{t("editRoom.description")}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="grid gap-5">
        <div className="grid gap-2">
          <Label htmlFor="edit-room-name">{t("editRoom.name")}</Label>
          <Input
            id="edit-room-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            autoFocus
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="edit-room-description">
            {t("editRoom.descriptionLabel")}
          </Label>
          <Textarea
            id="edit-room-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={240}
            placeholder={t("editRoom.descriptionPlaceholder")}
          />
          <p className="text-2xs text-ink-4 num">
            {description.trim().length}/240
          </p>
        </div>

        <div className="grid gap-2">
          <Label>{t("room.label", { ns: "plans" })}</Label>
          <RoomPlanLine boardId={room._id as Id<"boards">} />
          <RoomHouse room={room} />
        </div>

        <div className="grid gap-2">
          <Label>{t("editRoom.icon")}</Label>
          <div className="grid grid-cols-7 gap-1 sm:grid-cols-9">
            {ICON_PICKER_OPTIONS.map((option) => {
              const selected = icon === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() =>
                    setIcon((prev) =>
                      prev === option.key ? null : option.key,
                    )
                  }
                  title={t(option.labelKey, { ns: "home" })}
                  aria-label={t(option.labelKey, { ns: "home" })}
                  aria-pressed={selected}
                  className={cn(
                    "flex aspect-square items-center justify-center border text-ink-3 transition-colors rounded-md",
                    selected
                      ? "border-ink bg-ink text-paper"
                      : "border-line hover:border-ink-3 hover:text-ink",
                  )}
                >
                  <RoomIcon icon={option.key} className="size-3.5" />
                </button>
              );
            })}
          </div>
          <p className="text-2xs text-ink-4">
            {icon
              ? selectedIconOption
                ? t(selectedIconOption.labelKey, { ns: "home" })
                : t("editRoom.customIcon")
              : room.roomType
                ? t("editRoom.defaultIcon")
                : t("editRoom.noIcon")}
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            {tc("actions.cancel")}
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!trimmed || !dirty || saving}
          >
            {saving ? tc("actions.saving") : tc("actions.save")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

/** Which of the household's houses the room is part of; nothing shown before there is one. */
function RoomHouse({ room }: { room: Doc<"boards"> }) {
  const { t } = useTranslation("plans");
  const { t: tc } = useTranslation();
  const houses = useQuery(api.houses.listMine);
  const setRoomHouse = useMutation(api.houses.setRoomHouse);
  // The dialog holds the room as it was opened: keep the choice here.
  const [houseId, setHouseId] = useState<string>(room.houseId ?? "");
  if (!houses || houses.length === 0) return null;
  return (
    <label className="flex items-center gap-2 text-xs text-ink-3">
      {t("room.house")}
      <select
        value={houseId}
        onChange={(e) => {
          const next = e.target.value;
          setHouseId(next);
          setRoomHouse({ boardId: room._id, houseId: (next || null) as Id<"houses"> | null }).catch((error) => {
            setHouseId(room.houseId ?? "");
            toast.error(said(error, tc("status.somethingWentWrong")));
          });
        }}
        className="h-7 rounded-md border border-line bg-paper px-2 text-xs text-ink"
      >
        <option value="">{t("room.noHouse")}</option>
        {houses.map((house) => (
          <option key={house._id} value={house._id}>
            {house.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1">
      <dt className="text-2xs uppercase tracking-wider text-ink-3">{label}</dt>
      <dd className="text-sm text-ink [overflow-wrap:anywhere]">{value}</dd>
    </div>
  );
}

function RedirectTo({ to }: { to: "/onboarding" }) {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to });
  }, [navigate, to]);
  return <Splash subtitle={t("page.redirecting")} />;
}
